import type { CardDraft } from '../types';

/**
 * Черновик редактора в localStorage.
 *
 * Мини-аппа в вебвью Telegram может быть выгружена в любой момент — при
 * сворачивании, нехватке памяти, обрыве связи. Сервер спасает не всегда:
 * запрос мог не успеть уйти. Поэтому состояние пишется локально на каждое
 * изменение, синхронно и без сети, а серверное сохранение идёт поверх этого.
 */

const VERSION = 1;
const PREFIX = 'shoppable:draft:';

export type StoredDraft = {
  version: number;
  savedAt: number;
  collectionId?: string;
  caption: string;
  buttonText: string;
  cards: CardDraft[];
};

/** Ключ отдельный для новой подборки и для каждой редактируемой. */
export function draftKey(collectionId?: string): string {
  return `${PREFIX}${collectionId ?? 'new'}`;
}

export function saveDraft(
  collectionId: string | undefined,
  data: { caption: string; buttonText: string; cards: CardDraft[] },
): void {
  // Пустой черновик хранить незачем — иначе он будет всплывать при каждом входе.
  if (!data.caption.trim() && data.cards.length === 0 && !data.buttonText.trim()) {
    clearDraft(collectionId);
    return;
  }
  const payload: StoredDraft = {
    version: VERSION,
    savedAt: Date.now(),
    collectionId,
    caption: data.caption,
    buttonText: data.buttonText,
    cards: data.cards,
  };
  try {
    localStorage.setItem(draftKey(collectionId), JSON.stringify(payload));
  } catch {
    // Приватный режим или переполнение — потеря черновика не повод падать.
  }
}

export function loadDraft(collectionId?: string): StoredDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(collectionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (parsed.version !== VERSION || !Array.isArray(parsed.cards)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(collectionId?: string): void {
  try {
    localStorage.removeItem(draftKey(collectionId));
  } catch {
    /* см. выше */
  }
}

/**
 * Черновик новой подборки после её сохранения переезжает под настоящий id,
 * иначе он останется висеть и предложится при следующем создании.
 */
export function moveDraft(collectionId: string): void {
  const draft = loadDraft(undefined);
  clearDraft(undefined);
  if (draft) {
    saveDraft(collectionId, {
      caption: draft.caption,
      buttonText: draft.buttonText ?? '',
      cards: draft.cards,
    });
  }
}

export function formatSavedAt(ts: number): string {
  const seconds = Math.round((Date.now() - ts) / 1000);
  if (seconds < 10) return 'только что';
  if (seconds < 60) return `${seconds} с назад`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;
  return new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' }).format(ts);
}
